import { inject, provide, toRefs, ref } from 'vue';
import { constant, utils } from '@cpc/comment-sdk-common';
import {
  useLaunchReplyPage,
  useUser,
  useGlobalState,
  useNativeAbility,
} from '@cpc/comment-sdk-core-comment';
import wv, { showToast } from '@cpc/comment-sdk-jsapi';
import type { CommentItemInterface } from '@cpc/comment-sdk-types/src/comment';
import { useThrottleFn } from '@vueuse/core';

import { getCommentBlackRelation } from '../../assets/js/api';
import { useBrowserVersion } from '../useBrowserVersion';

const noop = function () {};

export interface CommentClickConfigInterface {
  directComment?: boolean;
  directReport?: boolean;
  resolve?: () => Promise<void>;
  reject?: () => Promise<void>;
}

export interface BeActiveOnCommentInterface {
  commentId: string;
  replyId: string;
  owner: { nickname: string };
}

export interface UseOnCommentClickInjectInterface {
  onCommentClick: (
    itemData: CommentItemInterface | BeActiveOnCommentInterface,
    config?: CommentClickConfigInterface,
  ) => Promise<void>;
  onCommentClickThrottle: (
    itemData: CommentItemInterface | BeActiveOnCommentInterface,
    config?: CommentClickConfigInterface,
  ) => void;
  onComment: (
    itemData: CommentItemInterface | BeActiveOnCommentInterface,
    extra?: Record<string, unknown>,
  ) => Promise<unknown>;
  sendCommentCount: (number: number) => void;
}

export function useOnCommentClickProvide(options = {}) {
  const { createError } = utils;
  const { feature, pageType, documentMeta, transparentParams, commentStatus, uid } = toRefs(
    useGlobalState(),
  );

  const { isBrowserVersionHigherThan } = useBrowserVersion();

  const { onLoginIfNeed } = useUser();

  const getOnCommentParams = (itemData: CommentItemInterface) => {
    return {
      commentId: itemData.commentId || itemData.id || '',
      replyId: itemData.commentId ? itemData.id : '',
      nickname: itemData.owner.nickname || '',
      beRepliedId: itemData.owner.id || '',
    };
  };

  const commentCount = ref(0);
  const sendCommentCount = (number: number) => {
    commentCount.value = number;
  };

  const { setCommentIdAndUser } = useNativeAbility();
  const onComment = (itemData: CommentItemInterface, extra = {}) => {
    const { commentId, replyId, nickname, beRepliedId } = getOnCommentParams(itemData);

    setCommentIdAndUser(commentId, replyId, beRepliedId, nickname, extra);
  };

  const { setLaunchReplyPageData } = useLaunchReplyPage();

  const goDetailPage = async (
    itemData: CommentItemInterface,
    config: CommentClickConfigInterface = {} as CommentClickConfigInterface,
  ) => {
    const { directComment = false } = config;

    await setLaunchReplyPageData(
      {
        ...itemData,
        replies: [],
      },
      directComment,
      commentCount.value,
    );

    const replyPageUrl = `${documentMeta.value.replyPageUrl + transparentParams.value}&commentId=${
      itemData.id
    }`;

    if (import.meta.env.MODE === 'development') {
      window.location.assign(replyPageUrl);

      return;
    }

    const params = [replyPageUrl, itemData.id, itemData.owner.nickname, itemData.replySize || 0];

    console.log('launchCommentReplyPage', JSON.stringify(params));

    return wv.asyncInvoke('launchCommentReplyPage', params);
  };

  const { showCommentOption: callNativeShowCommentOption } = useNativeAbility();

  const showCommentOption = (itemData: CommentItemInterface) => {
    return callNativeShowCommentOption(itemData);
  };

  const fetchFeedback = async (itemData: CommentItemInterface) => {
    const params = {
      targetUser: itemData.owner.id || '',
      source: documentMeta.value.dataSource,
      feedsBizType: 1,
    };

    return getCommentBlackRelation(params)
      .then((data) => {
        let error;

        if (data.has_block) {
          error = createError(30002, '您已拉黑此用户，无法对其评论');
        } else if (data.be_block) {
          error = createError(30003, '依据对方设置，目前无法对其评论');
        }

        return error ? Promise.reject(error) : Promise.resolve({});
      })
      .catch((error) => {
        console.log(error);

        if (error.code === 30002 || error.code === 30003) {
          showToast(error.message);
        } else {
          showToast('获取用户拉黑关系失败，请稍后再试');
        }

        throw error;
      });
  };

  const onCommentClick = async (
    itemData: CommentItemInterface,
    config: CommentClickConfigInterface = {} as CommentClickConfigInterface,
  ) => {
    console.log('onCommentClick', itemData, config);

    const { directComment = false, directReport = false } = config;
    const isSecondCommentDetail = pageType.value === constant.PageType.SECOND_COMMENT_DETAIL;
    const isSupportReplyReport = await wv.isMethodValid('onCommentClick');

    itemData.owner = itemData.owner;

    // if (
    //   feature.value.enableSecondCommentDetail &&
    //   !isSecondCommentDetail &&
    //   !directReport &&
    //   !directComment
    // ) {
    //   goDetailPage(itemData, config);

    //   return;
    // }

    if (commentStatus.value !== constant.CommentStatus.DEFAULT && !directReport) {
      showToast('系统维护中，评论暂不开放');

      return;
    }

    if (!directComment && isSupportReplyReport) {
      /**
       * 不调起负反馈的情况
       *  1、浏览器版本低于8.16 && 用户自己发出的评论
       *  2、浏览器版本高于或者等于8.16 && 用户自己发出的单独gif图
       */
      await onLoginIfNeed();

      if (!isBrowserVersionHigherThan('8.16', '', true) && uid.value === itemData.owner.id) {
        return;
      } else if (
        isBrowserVersionHigherThan('8.16', '', true) &&
        !itemData.content &&
        itemData.imageList.length > 0
      ) {
        return;
      }

      showCommentOption(itemData);
    } else {
      await onLoginIfNeed();

      fetchFeedback(itemData).then(() => {
        onComment(itemData, {
          parentContent: itemData.content,
          parentId: itemData.id,
        });
      });
    }
  };

  const onCommentClickThrottle = useThrottleFn(
    (
      itemData: CommentItemInterface,
      config: CommentClickConfigInterface = {} as CommentClickConfigInterface,
    ) => {
      onCommentClick(itemData, config)
        .then(config.resolve || noop)
        .catch(config.reject || noop);
    },
    800,
    false,
  );

  provide(constant.onCommentClickSymbol, {
    onCommentClick,
    onCommentClickThrottle,
    onComment,
    sendCommentCount,
  });
}

export const useOnCommentClickInject = () => {
  const context: UseOnCommentClickInjectInterface | undefined = inject(
    constant.onCommentClickSymbol,
  );

  if (!context) {
    throw new Error('useOnCommentClickInject must be used after useOnCommentClickProvide');
  }

  return context;
};
